/**
 * Pipeline Store
 *
 * Zustand store for managing pipeline state.
 * Updates are driven by REST polling in PipelineView.
 * (WebSocket disabled — CloudFront doesn't support it with legacy forwarded_values config)
 */

import { create } from "zustand";
import {
  PipelineStage,
  PipelineStatus,
  StageStatusInfo,
  PipelineStatusEvent,
  JobStartedEvent,
  JobProgressEvent,
  JobCompletedEvent,
  JobFailedEvent,
} from "../types";

// Pipeline stage metadata
export const PIPELINE_STAGES: Array<{
  id: PipelineStage;
  name: string;
  description: string;
}> = [
  { id: "extract", name: "Extract", description: "Scrape athlete profiles from data sources" },
  { id: "transform", name: "Transform", description: "Sync and validate data" },
  { id: "match", name: "Match", description: "Generate college-athlete matches" },
  { id: "load", name: "Load", description: "Publish matches to Supabase" },
];

// Default stage status
const createDefaultStageStatus = (): StageStatusInfo[] =>
  PIPELINE_STAGES.map((stage) => ({
    stage: stage.id,
    status: "pending",
  }));

interface PipelineState {
  // Pipeline state
  pipelineRunId: string | null;
  pipelineStatus: PipelineStatus;
  stages: StageStatusInfo[];
  currentStage: PipelineStage | null;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;

  // Actions
  reset: () => void;
  updateFromPipelineStatus: (event: PipelineStatusEvent) => void;
  updateFromJobStarted: (event: JobStartedEvent) => void;
  updateFromJobProgress: (event: JobProgressEvent) => void;
  updateFromJobCompleted: (event: JobCompletedEvent) => void;
  updateFromJobFailed: (event: JobFailedEvent) => void;
}

export const usePipelineStore = create<PipelineState>((set) => ({
  // Initial state
  pipelineRunId: null,
  pipelineStatus: "pending",
  stages: createDefaultStageStatus(),
  currentStage: null,
  startedAt: null,
  completedAt: null,
  error: null,

  // Reset pipeline state
  reset: () => {
    set({
      pipelineRunId: null,
      pipelineStatus: "pending",
      stages: createDefaultStageStatus(),
      currentStage: null,
      startedAt: null,
      completedAt: null,
      error: null,
    });
  },

  // Update from pipeline status event (full state sync)
  updateFromPipelineStatus: (event: PipelineStatusEvent) => {
    set({
      pipelineRunId: event.pipelineRunId,
      pipelineStatus: event.status,
      stages: event.stages,
      currentStage: event.currentStage || null,
      startedAt: event.startedAt || null,
      completedAt: event.completedAt || null,
      error: event.error || null,
    });
  },

  // Update from job started event
  updateFromJobStarted: (event: JobStartedEvent) => {
    set((state) => ({
      pipelineRunId: event.pipelineRunId || state.pipelineRunId,
      pipelineStatus: "running",
      currentStage: event.stage as PipelineStage,
      startedAt: state.startedAt || event.timestamp,
      stages: state.stages.map((s) =>
        s.stage === event.stage
          ? {
              ...s,
              status: "running",
              jobId: event.jobId,
              startedAt: event.timestamp,
              progress: { percent: 0, message: "Starting..." },
            }
          : s
      ),
    }));
  },

  // Update from job progress event
  updateFromJobProgress: (event: JobProgressEvent) => {
    set((state) => ({
      stages: state.stages.map((s) =>
        s.stage === event.stage
          ? {
              ...s,
              progress: {
                percent: event.percent,
                message: event.message,
              },
            }
          : s
      ),
    }));
  },

  // Update from job completed event
  updateFromJobCompleted: (event: JobCompletedEvent) => {
    set((state) => {
      const updatedStages = state.stages.map((s) =>
        s.stage === event.stage
          ? {
              ...s,
              status: "completed" as const,
              completedAt: event.timestamp,
              progress: { percent: 100, message: "Complete" },
            }
          : s
      );

      // Check if all stages are completed
      const allCompleted = updatedStages.every(
        (s) => s.status === "completed" || s.status === "skipped"
      );

      return {
        stages: updatedStages,
        pipelineStatus: allCompleted ? "completed" : state.pipelineStatus,
        completedAt: allCompleted ? event.timestamp : state.completedAt,
        currentStage: allCompleted ? null : state.currentStage,
      };
    });
  },

  // Update from job failed event
  updateFromJobFailed: (event: JobFailedEvent) => {
    set((state) => ({
      stages: state.stages.map((s) =>
        s.stage === event.stage
          ? {
              ...s,
              status: "failed" as const,
              error: event.error,
              completedAt: event.timestamp,
            }
          : s
      ),
      pipelineStatus: "failed",
      error: event.error,
      currentStage: null,
    }));
  },
}));

// Selector hooks for specific state slices
export const useStageStatus = (stageId: PipelineStage): StageStatusInfo | undefined =>
  usePipelineStore((state) => state.stages.find((s) => s.stage === stageId));

export const useIsStageRunning = (stageId: PipelineStage): boolean =>
  usePipelineStore((state) => state.stages.find((s) => s.stage === stageId)?.status === "running");

export const useIsPipelineRunning = (): boolean =>
  usePipelineStore((state) => state.pipelineStatus === "running");
